import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { acquireScrollLock } from "../../lib/scrollLock";
import { createPortal } from "react-dom";
import { useTabActive } from "../../lib/tabActive";
import { Icon } from "../ui/Icon";
import { MAX_SATIR, MAX_SUTUN, sayfayiIzgaraCevir, type Izgara } from "../../lib/sheetGrid";

/** Uzantıdan önizleme türü. Tek yerde çözülür — ikon, tuval ve başlık AYNI karara bakar. */
type OnizlemeTuru = "pdf" | "image" | "sheet" | "yok";
function turCoz(url: string): OnizlemeTuru {
  const ext = (url.split("?")[0]?.split(".").pop() ?? "").toLowerCase();
  if (ext === "pdf") return "pdf";
  if (["png", "jpg", "jpeg", "webp", "gif"].includes(ext)) return "image";
  if (["xlsx", "xls", "csv"].includes(ext)) return "sheet";
  return "yok"; // docx/doc — tarayıcı gösteremez, indirmeye yönlendirilir
}

/** Excel çerçevesi: solda satır numarası oluğu, üstte sütun harfi şeridi. */
const OLUK_PX = 40;
const BASLIK_PX = 21;
const TUVAL_PAY = 14;
/** Ölçek tabanı — bunun altında yazı okunmaz olur; o noktadan sonra yatay kaydırma devreye girer. */
const MIN_OLCEK = 0.3;

/** 0 → "A", 26 → "AA" — Excel sütun etiketi (aralık A sütunundan başlamayabilir). */
function sutunAdi(i: number): string {
  let ad = "";
  for (let n = i; n >= 0; n = Math.floor(n / 26) - 1) ad = String.fromCharCode(65 + (n % 26)) + ad;
  return ad;
}

/**
 * Evrak/görsel önizleme modalı (lightbox) — elit lightbox tasarımı.
 * Görsel koyu tuval üzerinde çerçevelenir; PDF iframe ile açılır; EXCEL/CSV tarayıcıda
 * SheetJS ile okunup tabloya dönüştürülür (tarayıcı xlsx'i kendiliğinden gösteremez).
 * Word (docx/doc) için tarayıcıda gerçek bir oluşturucu yok → indirmeye yönlendirilir.
 * ESC + backdrop + × ile kapanır.
 * Toolbar: Paylaş (Web Share API — WhatsApp/e-posta/…; dosya paylaşımı yoksa bağlantı/indirme fallback) · İndir · Kapat.
 */
export function FilePreviewModal({ url, onClose }: { url: string | null; onClose: () => void }) {
  const [sharing, setSharing] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  /** Excel/CSV önizlemesi: sayfa adları, seçili sayfa, ızgara, yükleniyor/hata. */
  const [sayfalar, setSayfalar] = useState<string[]>([]);
  const [aktifSayfa, setAktifSayfa] = useState<string | null>(null);
  const [izgara, setIzgara] = useState<Izgara | null>(null);
  const [tabloYuk, setTabloYuk] = useState(false);
  const [tabloHata, setTabloHata] = useState<string | null>(null);
  /** "Sayfaya sığdır" (varsayılan) ↔ gerçek boyut. Kullanıcı isteği: önizleme sayfaya SIĞMALI. */
  const [sigdir, setSigdir] = useState(true);
  const [olcek, setOlcek] = useState(1);
  /** Çalışma kitabı ve SheetJS önbelleği — sayfa sekmesi değişince dosya YENİDEN indirilmez. */
  const kitapRef = useRef<import("xlsx").WorkBook | null>(null);
  const xlsxRef = useRef<typeof import("xlsx") | null>(null);
  const tuvalRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!url) return;
    setMsg(null);
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    // Ortak ref-sayaçlı kilit — kendi save/restore'u Drawer'ınkiyle çakışıp sayfayı
    // kalıcı kilitliyordu (bkz. lib/scrollLock.ts).
    const release = acquireScrollLock();
    return () => { document.removeEventListener("keydown", onKey); release(); };
  }, [url, onClose]);

  /**
   * EXCEL/CSV → tablo. Dosya İNDİRİLİP tarayıcıda ayrıştırılır; sunucuya iş düşmez.
   * `xlsx` dinamik import: bu paket ~400 KB, yalnız Excel önizlerken yüklensin.
   */
  useEffect(() => {
    const tur = url ? turCoz(url) : "yok";
    if (!url || tur !== "sheet") { setIzgara(null); setSayfalar([]); setAktifSayfa(null); setTabloHata(null); kitapRef.current = null; return; }
    let iptal = false;
    setTabloYuk(true); setTabloHata(null); setIzgara(null); setSigdir(true); setOlcek(1);
    (async () => {
      try {
        const XLSX = await import("xlsx");
        const res = await fetch(url);
        if (!res.ok) throw new Error(`Dosya alınamadı (${res.status})`);
        const buf = await res.arrayBuffer();
        const csvMi = url.toLowerCase().includes(".csv");
        let wb;
        if (csvMi) {
          /**
           * CSV metindir ve Türkiye'de sıklıkla windows-1254 kaydedilir. Önce UTF-8 denenir;
           * bozuk karakter (U+FFFD) çıkarsa 1254 ile yeniden çözülür — yoksa "Ürün" gibi
           * başlıklar bozuk görünürdü.
           */
          let metin = new TextDecoder("utf-8").decode(buf);
          if (metin.includes("\uFFFD")) {
            try { metin = new TextDecoder("windows-1254").decode(buf); } catch { /* tarayıcı desteklemiyorsa UTF-8'de kal */ }
          }
          wb = XLSX.read(metin, { type: "string" });
        } else {
          // `cellStyles` ŞART: sütun genişlikleri (`!cols`) ve satır yükseklikleri (`!rows`)
          // yalnız bu bayrakla dolar. Unutulursa tüm sütunlar eşit genişlikte çıkar — yani
          // "Excel'deki görüntünün aynısı" isteği SESSİZCE karşılanmamış olur.
          wb = XLSX.read(buf, { type: "array", cellStyles: true });
        }
        if (iptal) return;
        xlsxRef.current = XLSX;
        kitapRef.current = wb;
        const adlar = wb.SheetNames ?? [];
        setSayfalar(adlar);
        setAktifSayfa(adlar[0] ?? null);
        yazIzgara(XLSX, wb, adlar[0]);
      } catch (e) {
        if (!iptal) setTabloHata(e instanceof Error ? e.message : "Excel dosyası okunamadı.");
      } finally {
        if (!iptal) setTabloYuk(false);
      }
    })();
    return () => { iptal = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  /** Seçili sayfayı ızgaraya çevirir (genişlik + birleştirme + hizalama DOSYADAN gelir). */
  function yazIzgara(XLSX: typeof import("xlsx"), wb: import("xlsx").WorkBook, ad?: string) {
    const ws = ad ? wb.Sheets[ad] : undefined;
    setIzgara(ws ? sayfayiIzgaraCevir(XLSX, ws) : null);
  }

  /** Sayfa sekmesi değişimi — kitap zaten bellekte, dosya YENİDEN İNDİRİLMEZ. */
  function sayfaSec(ad: string) {
    const wb = kitapRef.current, XLSX = xlsxRef.current;
    if (!wb || !XLSX) return;
    setAktifSayfa(ad);
    yazIzgara(XLSX, wb, ad);
  }

  /**
   * SAYFAYA SIĞDIR. Tablo doğal (Excel) boyutunda kurulur, sonra `transform: scale()` ile
   * tuvale sığacak kadar küçültülür — sütun genişlikleri arasındaki ORAN korunur.
   *
   * Not: dikey kaydırma çubuğu gelip gidince `clientWidth` değişir; ölçek → yükseklik →
   * çubuk → ölçek döngüsü kurulmasın diye tuvalde `scrollbar-gutter: stable` var ve ölçek
   * yalnız anlamlı bir fark varsa yazılır.
   */
  useLayoutEffect(() => {
    const el = tuvalRef.current;
    if (!el || !izgara || izgara.satirlar.length === 0) return;
    const dogal = OLUK_PX + izgara.toplamGenislik;
    const hesapla = () => {
      const alan = el.clientWidth - 2 * TUVAL_PAY;
      const yeni = sigdir && alan > 0 && dogal > alan ? Math.max(alan / dogal, MIN_OLCEK) : 1;
      setOlcek((onceki) => (Math.abs(onceki - yeni) < 0.002 ? onceki : yeni));
    };
    hesapla();
    const ro = new ResizeObserver(hesapla);
    ro.observe(el);
    return () => ro.disconnect();
  }, [izgara, sigdir]);

  const tabActive = useTabActive();
  if (!url) return null;
  // PASİF SEKMEDE DOM'A BASILMAZ. Portal `document.body`ye gider, yani sekme panelinin
  // `display:none` gizlemesi bu katmana ULAŞMAZ — gizli sekmenin paneli aktif sayfanın
  // üstünde durmaya devam ederdi. Burada `null` dönmek DOM'u düşürür, katmanı açan durum
  // (hangi kayıt, forma ne yazıldı) sayfanın React state'inde YAŞAMAYA DEVAM eder.
  if (!tabActive) return null;

  const tur = turCoz(url);
  const isPdf = tur === "pdf";
  // Tablonun DOĞAL (Excel) boyutu — sığdırma ölçeği ve sarmalayıcı kutusu bunu kullanır.
  const dogalGenislik = izgara ? OLUK_PX + izgara.toplamGenislik : 0;
  const dogalYukseklik = izgara ? BASLIK_PX + izgara.satirYukseklik.reduce((a, b) => a + b, 0) : 0;
  const genisTuval = tur === "pdf" || tur === "sheet"; // büyük tuval isteyen türler
  const fileName = url.split("/").pop() ?? "evrak";
  const canShareApi = typeof navigator !== "undefined" && !!navigator.share;

  async function onShare() {
    if (!url) return;
    setSharing(true);
    setMsg(null);
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error("Dosya alınamadı");
      const blob = await res.blob();
      const file = new File([blob], fileName, { type: blob.type || (isPdf ? "application/pdf" : "image/jpeg") });
      if (navigator.canShare?.({ files: [file] }) && navigator.share) {
        await navigator.share({ files: [file], title: fileName });
      } else if (navigator.share) {
        await navigator.share({ title: fileName, url }); // dosya paylaşımı yoksa bağlantıyı paylaş
      } else {
        const a = document.createElement("a"); a.href = url; a.download = fileName; document.body.appendChild(a); a.click(); a.remove();
        setMsg("Cihaz doğrudan paylaşımı desteklemiyor — dosya indirildi.");
      }
    } catch (e) {
      if ((e as { name?: string })?.name !== "AbortError") setMsg("Paylaşım tamamlanamadı.");
    } finally {
      setSharing(false);
    }
  }

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Evrak önizleme"
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 3000,
        background: "rgba(9,14,26,0.80)", backdropFilter: "blur(6px)",
        display: "flex", alignItems: "center", justifyContent: "center", padding: "clamp(12px, 3vw, 32px)",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          display: "flex", flexDirection: "column",
          width: genisTuval ? "min(1100px, 96vw)" : "auto", maxWidth: "96vw",
          height: genisTuval ? "min(90vh, 1100px)" : "auto", maxHeight: "94vh",
          background: "var(--white)", borderRadius: 16, overflow: "hidden",
          boxShadow: "0 30px 80px rgba(0,0,0,0.55)", border: "1px solid rgba(255,255,255,0.06)",
        }}
      >
        {/* Toolbar */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "11px 12px 11px 14px", borderBottom: "1px solid var(--neutral-100)", background: "var(--white)" }}>
          <span style={{ flexShrink: 0, width: 34, height: 34, borderRadius: 9, background: "var(--primary-subtle)", color: "var(--primary)", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
            <Icon name={tur === "pdf" ? "fileText" : tur === "sheet" ? "table" : tur === "image" ? "image" : "fileText"} size={17} />
          </span>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "var(--neutral-900)", lineHeight: 1.2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {tur === "pdf" ? "Belge" : tur === "sheet" ? "Tablo" : tur === "image" ? "Görsel" : "Dosya"}
            </div>
            <div className="mono" style={{ fontSize: 11, color: "var(--neutral-500)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginTop: 1 }} title={fileName}>{fileName}</div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
            {/* Sığdırma zaten varsayılan; ölçek 1'in altına indiyse kullanıcı gerçek boyuta
                dönüp yakınlaşabilsin — küçük yazıyı okumanın tek yolu bu. */}
            {tur === "sheet" && izgara && izgara.satirlar.length > 0 && (sigdir ? olcek < 1 : true) ? (
              <button
                type="button" className="btn-md secondary" onClick={() => setSigdir((v) => !v)}
                title={sigdir ? "Gerçek boyutta göster (%100)" : "Sayfaya sığdır"}
              >
                <Icon name={sigdir ? "maximize" : "minimize"} size={14} />
                {sigdir ? `%${Math.round(olcek * 100)}` : "Sığdır"}
              </button>
            ) : null}
            {canShareApi ? (
              <button type="button" className="btn-md secondary" onClick={onShare} disabled={sharing} title="Paylaş — WhatsApp, e-posta, kaydet…">
                <Icon name="share" size={14} />{sharing ? "Hazırlanıyor…" : "Paylaş"}
              </button>
            ) : null}
            <a href={url} download={fileName} className="btn-md secondary" style={{ textDecoration: "none" }} title="İndir"><Icon name="download" size={14} />İndir</a>
            <button type="button" className="iact" aria-label="Kapat" title="Kapat (Esc)" onClick={onClose} style={{ marginLeft: 2 }}><Icon name="x" size={16} /></button>
          </div>
        </div>

        {msg ? (
          <div style={{ padding: "7px 16px", fontSize: 11.5, color: "var(--neutral-600)", background: "var(--neutral-50)", borderBottom: "1px solid var(--neutral-100)" }}>{msg}</div>
        ) : null}

        {/* Excel/CSV: sayfa sekmeleri — yalnız birden çok sayfa varsa */}
        {tur === "sheet" && sayfalar.length > 1 ? (
          <div style={{ display: "flex", gap: 4, padding: "6px 12px", borderBottom: "1px solid var(--neutral-100)", background: "var(--neutral-50)", overflowX: "auto" }}>
            {sayfalar.map((ad) => (
              <button
                key={ad} type="button"
                onClick={() => sayfaSec(ad)}
                className={`m-chip${ad === aktifSayfa ? " act" : ""}`}
                style={{ whiteSpace: "nowrap" }}
              >{ad}</button>
            ))}
          </div>
        ) : null}
        {tur === "sheet" && izgara?.kirpildi ? (
          <div style={{ padding: "7px 16px", fontSize: 11.5, color: "var(--neutral-600)", background: "var(--warning-subtle, var(--neutral-50))", borderBottom: "1px solid var(--neutral-100)" }}>
            Önizleme ilk <b>{MAX_SATIR}</b> satır ve <b>{MAX_SUTUN}</b> sütunla sınırlı
            (dosyada {izgara.kirpildi.satir} satır · {izgara.kirpildi.sutun} sütun var). Tamamı için indirin.
          </div>
        ) : null}

        {/* Tuval */}
        <div style={{ flex: 1, minHeight: 0, display: "flex", alignItems: tur === "sheet" ? "stretch" : "center", justifyContent: "center", background: genisTuval ? "var(--neutral-100)" : "#0b1120", overflow: tur === "sheet" ? "hidden" : "auto", padding: genisTuval ? 0 : "clamp(12px, 2vw, 26px)" }}>
          {tur === "pdf" ? (
            <iframe src={url} title="PDF önizleme" style={{ width: "100%", height: "100%", border: 0, display: "block" }} />
          ) : tur === "image" ? (
            <img src={url} alt="Evrak önizleme" style={{ maxWidth: "100%", maxHeight: "82vh", objectFit: "contain", display: "block", borderRadius: 8, boxShadow: "0 10px 34px rgba(0,0,0,0.45)" }} />
          ) : tur === "sheet" ? (
            tabloYuk ? (
              <div style={{ margin: "auto", color: "var(--neutral-500)", fontSize: 13 }}>Tablo okunuyor…</div>
            ) : tabloHata ? (
              <div style={{ margin: "auto", textAlign: "center", color: "var(--neutral-600)", fontSize: 13, padding: 24 }}>
                <div style={{ marginBottom: 8 }}>{tabloHata}</div>
                <a href={url} download={fileName} className="btn-md secondary" style={{ textDecoration: "none" }}><Icon name="download" size={14} />Dosyayı indir</a>
              </div>
            ) : izgara && izgara.satirlar.length > 0 ? (
              /**
               * EXCEL GÖRÜNÜMÜ. Sütun genişlikleri `<colgroup>` ile DOSYADAN gelir ve tablo
               * `table-layout: fixed` olduğu için içerik onları esnetemez. Doğal boyutta kurulan
               * bu tablo, tuvale sığmıyorsa `scale()` ile küçültülür — oranlar bozulmadan.
               */
              <div
                ref={tuvalRef}
                style={{ width: "100%", height: "100%", overflow: "auto", scrollbarGutter: "stable", background: "var(--neutral-100)", padding: TUVAL_PAY }}
              >
                {/* Dış kutu ÖLÇEKLENMİŞ boyutu kaplar: `transform` düzen akışını değiştirmediği
                    için bu olmadan küçültülmüş tablonun altında/yanında ölü boşluk kalırdı. */}
                <div style={{ width: dogalGenislik * olcek, height: dogalYukseklik * olcek, position: "relative" }}>
                  <div style={{ position: "absolute", top: 0, left: 0, width: dogalGenislik, transform: `scale(${olcek})`, transformOrigin: "top left", background: "var(--white)", boxShadow: "0 2px 12px rgba(0,0,0,0.10)" }}>
                    <table style={{ tableLayout: "fixed", width: dogalGenislik, borderCollapse: "collapse", fontSize: 12.5, color: "var(--neutral-800)" }}>
                      <colgroup>
                        <col style={{ width: OLUK_PX }} />
                        {izgara.sutunGenislik.map((g, j) => <col key={j} style={{ width: g }} />)}
                      </colgroup>
                      <thead>
                        {/* Sütun harfi şeridi — Excel'in kendi başlığı; hangi sütuna baktığını gösterir. */}
                        <tr style={{ height: BASLIK_PX }}>
                          <th style={{ background: "var(--neutral-100)", borderRight: "1px solid var(--neutral-300)", borderBottom: "1px solid var(--neutral-300)" }} />
                          {izgara.sutunGenislik.map((_, j) => (
                            <th key={j} className="mono" style={{ background: "var(--neutral-100)", color: "var(--neutral-500)", fontSize: 10, fontWeight: 600, textAlign: "center", borderRight: "1px solid var(--neutral-300)", borderBottom: "1px solid var(--neutral-300)", userSelect: "none" }}>
                              {sutunAdi(izgara.ilkSutun + j)}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {izgara.satirlar.map((satir, i) => (
                          <tr key={i} style={{ height: izgara.satirYukseklik[i] }}>
                            <th className="mono" style={{ background: "var(--neutral-100)", color: "var(--neutral-500)", fontSize: 10, fontWeight: 600, textAlign: "center", borderRight: "1px solid var(--neutral-300)", borderBottom: "1px solid var(--neutral-200)", userSelect: "none" }}>
                              {izgara.ilkSatir + i + 1}
                            </th>
                            {satir.map((h, j) => h.ortulu ? null : (
                              <td
                                key={j} rowSpan={h.rowSpan} colSpan={h.colSpan}
                                title={h.metin || undefined}
                                className={h.sayisal ? "mono" : undefined}
                                style={{
                                  padding: "0 5px", verticalAlign: "middle",
                                  borderRight: "1px solid var(--neutral-200)", borderBottom: "1px solid var(--neutral-200)",
                                  textAlign: h.sayisal ? "right" : "left",
                                  whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                                }}
                              >{h.metin}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            ) : (
              <div style={{ margin: "auto", color: "var(--neutral-500)", fontSize: 13 }}>Bu sayfa boş.</div>
            )
          ) : (
            /* docx/doc — tarayıcıda gösterilemez. Kırık görsel yerine NE YAPILACAĞI söylenir. */
            <div style={{ margin: "auto", textAlign: "center", color: "var(--neutral-600)", fontSize: 13, padding: 32 }}>
              <Icon name="fileText" size={28} />
              <div style={{ margin: "10px 0 14px" }}>Bu dosya türü tarayıcıda önizlenemiyor.</div>
              <a href={url} download={fileName} className="btn-md primary" style={{ textDecoration: "none" }}><Icon name="download" size={14} />İndir</a>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
